"use client";

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { SilaeCodeEditor } from "./silae-code-editor";

interface CodeEditDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  initialCode: string;
  onSave?: (editedCode: string) => void;
  onCopy?: (code: string) => void;
  readOnly?: boolean;
  title?: string;
  description?: string;
  placeholder?: string;
  language?: string;
}

/**
 * Composant réutilisable pour l'édition de code dans un dialog
 */
export const CodeEditDialog: React.FC<CodeEditDialogProps> = ({
  isOpen,
  onOpenChange,
  initialCode,
  onSave,
  onCopy,
  readOnly = false,
  title,
  description,
  placeholder,
}) => {
  const { t } = useTranslation();
  const [editedCode, setEditedCode] = useState(initialCode);

  // Utiliser les traductions par défaut si les props ne sont pas fournies
  const dialogTitle = title || t("codeEditDialog.title");
  const dialogDescription = description || t("codeEditDialog.description");
  const dialogPlaceholder = placeholder || t("codeEditDialog.placeholder");

  // Mettre à jour le code édité quand le code initial change
  useEffect(() => {
    setEditedCode(initialCode);
  }, [initialCode]);

  const handleSave = () => {
    if (readOnly || !onSave) return;
    onSave(editedCode);
    onOpenChange(false);
  };

  const handleCopy = async () => {
    if (onCopy) {
      onCopy(editedCode);
    } else {
      // Fallback: copier directement dans le presse-papiers
      try {
        await navigator.clipboard.writeText(editedCode);
        toast.success(
          t("codeEditDialog.copySuccess") || "Code copied to clipboard!",
        );
      } catch (error) {
        console.error("Failed to copy code:", error);
        toast.error(t("codeEditDialog.copyError") || "Failed to copy code");
      }
    }
  };

  const handleCancel = () => {
    setEditedCode(initialCode);
    onOpenChange(false);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      // Si on ferme le dialog, réinitialiser le code
      setEditedCode(initialCode);
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="min-w-4xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-hidden">
          <SilaeCodeEditor
            value={editedCode}
            onChange={setEditedCode}
            placeholder={dialogPlaceholder}
            className="w-full min-h-[600px]"
            isInDialog={true}
            readOnly={readOnly}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            {readOnly ? t("common.close") : t("codeEditDialog.cancel")}
          </Button>
          <Button variant="outline" onClick={handleCopy}>
            <Copy className="h-4 w-4 mr-2" />
            {t("codeEditDialog.copy")}
          </Button>
          {!readOnly && onSave && (
            <Button onClick={handleSave}>{t("codeEditDialog.save")}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
